import React, { useCallback, useEffect, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * Session 30.13 — Full-screen toggle for the Retail POS.
 *
 * Browser / Android WebView → Fullscreen API on <html>.
 * Windows EXE (Tauri)       → window.setFullscreen(), which is far more
 *                             reliable in WebView2 than the DOM API.
 * Exit is always one tap away: the same button flips to "Exit full
 * screen" and Esc keeps working in browsers.
 */
async function _tauriWindow() {
  try {
    const w = typeof window !== "undefined" ? window : null;
    if (!w || (!w.__TAURI_INTERNALS__ && !w.__TAURI__)) return null;
    const mod = await import("@tauri-apps/api/window").catch(() => null);
    if (!mod) return null;
    const get = mod.getCurrentWindow || mod.getCurrent;
    return typeof get === "function" ? get() : null;
  } catch (_) { return null; }
}

function _domFsElement() {
  if (typeof document === "undefined") return null;
  return document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement || null;
}

export function FullscreenButton({ className = "", testId = "fullscreen-toggle-button" }) {
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const sync = async () => {
      const tw = await _tauriWindow();
      if (tw && typeof tw.isFullscreen === "function") {
        try { setIsFs(Boolean(await tw.isFullscreen())); return; } catch (_) { /* fall through */ }
      }
      setIsFs(Boolean(_domFsElement()));
    };
    sync();
    const events = ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"];
    events.forEach((e) => document.addEventListener(e, sync));
    const t = setInterval(sync, 2000); // Tauri has no DOM event for window fullscreen
    return () => { events.forEach((e) => document.removeEventListener(e, sync)); clearInterval(t); };
  }, []);

  const toggle = useCallback(async () => {
    try {
      const tw = await _tauriWindow();
      if (tw && typeof tw.setFullscreen === "function") {
        const cur = typeof tw.isFullscreen === "function" ? await tw.isFullscreen() : isFs;
        await tw.setFullscreen(!cur);
        setIsFs(!cur);
        return;
      }
      if (typeof document === "undefined") return;
      const el = document.documentElement;
      if (!_domFsElement()) {
        const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
        if (!req) { toast.message("Full screen is not supported here."); return; }
        await req.call(el);
      } else {
        const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
        if (exit) await exit.call(document);
      }
    } catch (err) {
      toast.error(err && err.message ? `Full screen unavailable: ${err.message}` : "Full screen is not available right now.");
    }
  }, [isFs]);

  const label = isFs ? "Exit full screen" : "Full screen";
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={`h-10 w-10 rounded-xl ${className}`}
      onClick={toggle}
      aria-label={label}
      title={label}
      data-testid={testId}
    >
      {isFs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
    </Button>
  );
}
